import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  Alert,
  Divider,
  CircularProgress,
} from '@mui/material';
import { Upload as UploadIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import config from '../config';

function ProductLocationsUpload() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [pastedData, setPastedData] = useState('');

  const validateCSV = (data) => {
    try {
      const lines = data.trim().split('\n');
      if (lines.length < 2) {
        throw new Error('CSV must include header row and at least one data row');
      }
      return true;
    } catch (error) {
      setError(error.message);
      return false;
    }
  };

  const handleUpload = async () => {
    try {
      if (!validateCSV(pastedData)) {
        return;
      }
      
      console.log('Data size:', new Blob([pastedData]).size, 'bytes');
      
      // Parse CSV into array of objects
      const lines = pastedData.trim().split('\n');
      const headers = lines[0].split(',').map(h => h.trim());
      const rows = lines.slice(1).map(line => {
        // Parse CSV line handling quotes and commas
        const values = [];
        let currentValue = '';
        let insideQuotes = false;
        
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          
          if (char === '"') {
            if (insideQuotes && line[i + 1] === '"') {
              // Handle escaped quotes
              currentValue += '"';
              i++; // Skip next quote
            } else {
              // Toggle quote mode
              insideQuotes = !insideQuotes;
            }
          } else if (char === ',' && !insideQuotes) {
            // End of field
            values.push(currentValue.trim());
            currentValue = '';
          } else {
            currentValue += char;
          }
        }
        // Push the last value
        values.push(currentValue.trim());
        
        return headers.reduce((obj, header, index) => {
          obj[header] = values[index] || '';
          return obj;
        }, {});
      });
      
      // Send in chunks of 100 rows
      const chunkSize = 100;
      const chunks = [];
      for (let i = 0; i < rows.length; i += chunkSize) {
        chunks.push(rows.slice(i, i + chunkSize));
      }
      
      setLoading(true);
      setError(null);
      setMessage(null);
      
      let totalUploaded = 0;
      
      // Upload each chunk
      for (let i = 0; i < chunks.length; i++) {
        const response = await fetch(`${config.apiUrl}/productlocations/upload`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ 
            data: chunks[i],
            isFirstChunk: i === 0,
            isLastChunk: i === chunks.length - 1,
            headers
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to upload chunk ${i + 1}`);
        }

        const result = await response.json();
        totalUploaded += chunks[i].length;

        // Update progress
        setMessage(`Uploaded ${totalUploaded} of ${rows.length} records...`);
      }

      setMessage(`Successfully uploaded ${totalUploaded} records`);
      setPastedData('');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Product Locations Data
      </Typography>
      
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" paragraph>
          Paste CSV data from your spreadsheet:
        </Typography>
        <Typography component="ul" variant="body2" color="text.secondary" sx={{ pl: 2 }}>
          <li>Include a header row</li>
          <li>Use comma (,) as the delimiter</li>
          <li>All existing data will be replaced</li>
          <li>Example format:</li>
        </Typography>
        <Typography 
          variant="caption" 
          component="pre" 
          sx={{ 
            bgcolor: 'grey.100', 
            p: 1, 
            borderRadius: 1,
            whiteSpace: 'pre-wrap'
          }}
        >
          Part Number,Description,Location,Quantity,Notes
          ABC123,Widget,Shelf A-1,50,High demand
          XYZ789,Gadget,Bin B-2,25,Backup stock
        </Typography>
      </Box>
      
      <Divider sx={{ my: 2 }} />
      
      <TextField
        multiline
        rows={10}
        fullWidth
        value={pastedData}
        onChange={(e) => setPastedData(e.target.value)}
        placeholder="Paste CSV data here..."
        sx={{ mb: 2 }}
      />

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <UploadIcon />}
          onClick={handleUpload}
          disabled={loading || !pastedData.trim()}
        >
          Upload Data
        </Button>
        {message && <Alert severity="success">{message}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
      </Box>
    </Paper>
  );
}

export default ProductLocationsUpload; 